import { useState } from "react";
import { updateProfile, addFriend, removeFriend, sendNotification } from "../api/users";
import { getPlanetStyle, colorsFromHue, hueOf } from "../utils/format";
import { COLOR_UNLOCK, TRAVELER_UNLOCK, FOX_UNLOCK } from "../data/constants";
import PlanetTraveler from "../components/PlanetTraveler";
import PlanetFox from "../components/PlanetFox";
import tasteSummary from "../utils/tasteSummary";
import MonthlyChart from "../components/MonthlyChart";
import { readBooks } from "../utils/bookState";
import { TYPES } from "../data/bookPti";
const AVATARS = ["🌿","🌙","⭐","🔮","🌞","🌊","🔥","❄️","🌸","🔭","🗺️","🪐","🌈","🍀","🦊"];

/** 30도 간격 12색. 브랜드 공식으로 만들어 어떤 색을 골라도 톤이 맞는다. */
const HUES = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

const ProfileHero = ({ user, isMe, currentUser, myBooks, allBooks = [], onGoQuotes, myUser, myFriends = [], similarity, isFriend, onUserUpdated, reloadWishlist, onShowDeleteModal }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editAvatar, setEditAvatar] = useState("");
  const [editHue, setEditHue] = useState(null);
  const [saving, setSaving] = useState(false);

  // 담아둔 책은 읽은 책이 아니다 — 읽은 책/읽는 중/공통 책에서 빼둔다.
  const myReadBooks = readBooks(myBooks, currentUser?.uid);
  // 행성 색 잠금은 "읽은 권수" 가 아니라 서재에 담은 권수 기준이다.
  // 홈(PlanetSection)의 안내 문구와 같은 수를 써야 어긋나지 않는다.
  const bookCount = myBooks?.length || 0;
  const canPickColor = isMe && bookCount >= COLOR_UNLOCK;
  /* 여행자는 남의 행성에서도 보인다. 남의 것은 bookIds(담아둔 책 제외)로만
     셀 수 있어, 내 것도 담아둔 책을 뺀 수로 맞춘다. */
  const planetCount = isMe ? myReadBooks.length : (user.bookIds || []).length;
  const travelerHere = planetCount >= TRAVELER_UNLOCK;
  const foxHere = planetCount >= FOX_UNLOCK;

  const startEdit = () => {
    setEditName(user.name || "");
    setEditAvatar(user.avatar || "🌿");
    setEditHue(hueOf(user.planetColors?.[0]));
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      // 표시 이름은 profiles 행이 단일 출처다.
      // 예전엔 Firebase Auth 프로필과 Firestore 문서 두 곳에 나눠 써야 했다.
      await updateProfile(currentUser.uid, {
        name: editName.trim(),
        handle: editName.trim().toLowerCase().replace(/\s+/g, "_"),
        avatar: editAvatar,
        // 잠금이 풀린 사람이 색을 고른 경우에만 함께 저장한다
        ...(canPickColor && editHue !== null ? { planetColors: colorsFromHue(editHue) } : {}),
      });
      if (onUserUpdated) onUserUpdated();
    } catch (e) {
      console.error("Profile update failed:", e);
    }
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="user-hero">
      {editing ? (
        <div className="profile-edit">
          <div className="avatar-picker">
            {AVATARS.map(a => (
              <button
                key={a}
                className={`avatar-option${editAvatar === a ? " selected" : ""}`}
                onClick={() => setEditAvatar(a)}
              >{a}</button>
            ))}
          </div>
          <input
            className="profile-edit-input"
            placeholder="이름 (닉네임)"
            value={editName}
            onChange={e => setEditName(e.target.value)}
          />

          {/* 20권을 넘기면 행성 색을 직접 고를 수 있다 */}
          {isMe && (
            <div className="color-picker-wrap">
              <div className="color-picker-label">
                행성 색
                {!canPickColor && (
                  <span className="color-locked">
                    · {COLOR_UNLOCK}권부터 · 지금 {bookCount}권
                  </span>
                )}
              </div>
              {canPickColor ? (
                <div className="color-picker">
                  {HUES.map(h => {
                    const [c1, c2, c3] = colorsFromHue(h);
                    return (
                      <button
                        key={h}
                        className={`color-option${editHue === h ? " selected" : ""}`}
                        style={{ background: `radial-gradient(circle at 35% 30%, ${c1}, ${c2} 55%, ${c3})` }}
                        onClick={() => setEditHue(h)}
                        aria-label={`색조 ${h}도`}
                        aria-pressed={editHue === h}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="color-locked-bar">
                  <div
                    className="color-locked-fill"
                    style={{ width: `${Math.min(100, (bookCount / COLOR_UNLOCK) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}
          <div className="profile-edit-actions">
            <button className="btn-save" onClick={saveEdit} disabled={saving || !editName.trim()}>
              {saving ? "저장 중..." : "저장"}
            </button>
            <button className="btn-cancel" onClick={() => setEditing(false)}>취소</button>
          </div>
        </div>
      ) : (
        <>
          <div className="user-planet-big" style={getPlanetStyle(user)}>
            {user.avatar}
            {travelerHere && <PlanetTraveler size="profile" />}
            {foxHere && <PlanetFox size="profile" />}
          </div>
          <h2>{user.name}</h2>
          <div className="user-handle">@{user.handle}</div>
          {/* 북BTI 유형. 한 사람이 어떤 독자인지 한 줄로 알리는 자리라
              이름 바로 아래에 둔다. 아직 안 한 사람에게는 아무것도 없다. */}
          {user.bookType && TYPES[user.bookType] && (
            <div className="pti-badge">
              <span aria-hidden="true">{TYPES[user.bookType].icon}</span>
              {TYPES[user.bookType].name}
            </div>
          )}
          {/* 서재를 한 문장으로. 감정 좌표가 5권 미만이면 아무 말도 하지 않는다 —
              두세 권으로 사람의 취향을 단정하면 틀린다. */}
          {!currentUser?.isAnonymous && (() => {
            const t = tasteSummary(user, allBooks);
            if (!t) return null;
            return (
              <div className="taste-box">
                <div className="taste-line">{t.sentence}</div>
                <div className="taste-basis">{t.basis}</div>
              </div>
            );
          })()}
          {/* 게스트에게는 감춘다. 이름을 가린 것과 같은 기준이다. */}
          {/* 문장이 없으면 걸 자리를 보여준다. 프로필 수정 안에만 두면
              아무도 못 찾고, 그러면 '오늘의 문장' 도 영원히 안 뜬다. */}
          {isMe && !user.quote && (
            <button
              className="planet-quote-empty"
              onClick={() => onGoQuotes?.()}
            >
              <span className="planet-quote-empty-mark" aria-hidden="true">“</span>
              기억에 남은 문장을 행성에 걸어보세요
            </button>
          )}
          {user.quote && !currentUser?.isAnonymous && (
            <blockquote className="planet-quote">
              <p className="planet-quote-text">{user.quote.text}</p>
              <cite className="planet-quote-src">
                『{user.quote.bookTitle}』{user.quote.bookAuthor ? ` ${user.quote.bookAuthor}` : ""}
              </cite>
            </blockquote>
          )}
          {isMe && (
            <div className="profile-actions-row">
              <button className="btn-edit-profile" onClick={startEdit}>프로필 수정</button>
              <button className="btn-delete-account" onClick={onShowDeleteModal}>회원 탈퇴</button>
            </div>
          )}
          {!isMe && !currentUser?.isAnonymous && (() => {
            const isMF = myFriends.includes(user.id);
            if (isFriend) {
              return <button className="btn-toggle-friend is-friend" disabled>✓ 이웃 행성</button>;
            }
            return (
              <button
                className={`btn-toggle-friend${isMF ? " is-friend" : ""}`}
                onClick={async () => {
                  const uid = currentUser.uid;
                  if (isMF) {
                    await removeFriend(uid, user.id);
                  } else {
                    await addFriend(uid, user.id);
                    await sendNotification({ toUserId: user.id, fromUserId: uid, type: "friend" });
                  }
                  if (reloadWishlist) reloadWishlist();
                }}
              >
                {isMF ? "✓ 이웃 행성" : "+ 이웃 행성 추가"}
              </button>
            );
          })()}
        </>
      )}
      {/* 남이 내 행성에 와도 같은 그래프를 본다. 홈에서는 내 서재로
          그리지만 여기서는 그 행성 주인의 서재로 그린다.
          게스트에게는 감춘다 — 이름·문장을 가린 것과 같은 기준이다. */}
      {!currentUser?.isAnonymous && !editing && (
        <MonthlyChart
          books={(allBooks || []).filter((b) => (user.bookIds || []).includes(b.id))}
          uid={user.id}
        />
      )}
      <div className="user-stats">
        {isMe ? (
          <>
            <div><span className="stat-num">{myReadBooks.filter(b => b.completedBy && b.completedBy[currentUser?.uid]).length}</span><span className="stat-label">읽은 책</span></div>
            <div><span className="stat-num" style={{color:"#e89b5a"}}>{myReadBooks.filter(b => !b.completedBy || !b.completedBy[currentUser?.uid]).length}</span><span className="stat-label" style={{color:"#e89b5a"}}>읽는 중</span></div>
          </>
        ) : (
          <>
            <div><span className="stat-num">{(user.bookIds || []).length}</span><span className="stat-label">등록 책</span></div>
            <div><span className="stat-num" style={{color: similarity >= 30 ? "var(--teal)" : "var(--glow)"}}>{similarity}%</span><span className="stat-label">취향 유사도</span></div>
            {(() => {
              const myBookIds = myReadBooks.map(b => b.id);
              const sharedCount = (user.bookIds || []).filter(id => myBookIds.includes(id)).length;
              return sharedCount > 0 ? (
                <div><span className="stat-num" style={{color:"var(--gold)"}}>{sharedCount}</span><span className="stat-label">공통 책</span></div>
              ) : null;
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileHero;
